import { Injectable } from '@angular/core';
import { TOSSFACE_CSS_PATH } from './tossface-css-path';

/**
 * FontLoaderService
 *
 * Asynchronously loads web fonts to prevent render-blocking.
 * Implements best practices for web font optimization:
 * - Preconnect to font CDNs for faster DNS/TCP/TLS handshake
 * - Load fonts asynchronously using media="print" trick
 * - Provides graceful fallback to system fonts during loading
 */
@Injectable({
  providedIn: 'root',
})
export class FontLoaderService {
  private fontsLoaded = false;

  constructor() {}

  /**
   * Load all web fonts asynchronously
   * Should be called early in app initialization (e.g., AppComponent constructor)
   */
  loadFonts(): void {
    if (this.fontsLoaded) {
      return;
    }

    this.addPreconnects();
    this.loadPretendardFont();
    this.loadInterFont();
    this.loadTossfaceFont();

    this.fontsLoaded = true;
  }

  /**
   * Add preconnect links to speed up font CDN connections
   */
  private addPreconnects(): void {
    const preconnects = [
      { href: 'https://fonts.googleapis.com', crossOrigin: true },
      { href: 'https://fonts.gstatic.com', crossOrigin: true },
    ];

    preconnects.forEach(({ href, crossOrigin }) => {
      if (!this.linkExists(href, 'preconnect')) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        if (crossOrigin) {
          link.crossOrigin = 'anonymous';
        }
        document.head.appendChild(link);
      }
    });
  }

  /**
   * Load Pretendard Variable font (Korean)
   * Dynamic subset for optimal performance
   */
  private loadPretendardFont(): void {
    this.loadStylesheet(
      'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css',
      'pretendard-font',
    );
  }

  /**
   * Load Inter font (English)
   * Only load weights actually used in the app: 400, 500, 600, 700
   * Note: display=swap prevents FOIT (Flash of Invisible Text)
   */
  private loadInterFont(): void {
    this.loadStylesheet(
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
      'inter-font',
    );
  }

  /**
   * Load Tossface emoji font
   * Unicode-range is defined in the font CSS to apply only to emojis
   */
  private loadTossfaceFont(): void {
    this.loadStylesheet(TOSSFACE_CSS_PATH, 'tossface-font');
  }

  /**
   * Asynchronously load a stylesheet using the media="print" trick
   * This prevents the stylesheet from blocking page rendering
   *
   * @param href - URL of the stylesheet
   * @param id - Unique ID for the link element
   */
  private loadStylesheet(href: string, id: string): void {
    if (this.linkExists(href, 'stylesheet')) {
      return;
    }

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    // Start with media="print" to load without blocking render
    link.media = 'print';
    link.onload = () => {
      // Switch to media="all" once loaded
      link.media = 'all';
    };
    link.onerror = () => {
      console.warn(`Failed to load font stylesheet: ${href}`);
      // Font will fallback to system fonts as defined in styles.scss
    };
    document.head.appendChild(link);
  }

  /**
   * Check if a link element already exists
   *
   * @param href - The href to check
   * @param rel - The rel attribute value
   * @returns true if link exists, false otherwise
   */
  private linkExists(href: string, rel: string): boolean {
    return !!document.querySelector(`link[href="${href}"][rel="${rel}"]`);
  }
}
